import React from 'react';

/**
 * Card
 *
 * Clean white card with optional header, footer, padding variants.
 *
 * Usage:
 *   <Card>Content</Card>
 *   <Card padding="none">
 *     <Card.Header title="Users" action={<Button>Invite</Button>} />
 *     <Card.Body>...</Card.Body>
 *   </Card>
 */
export function Card({
  padding = 'md',   // none | sm | md | lg
  className = '',
  children,
  ...props
}) {
  const paddings = {
    none: '',
    sm:   'p-4',
    md:   'p-5 sm:p-6',
    lg:   'p-6 sm:p-8',
  };

  return (
    <div
      className={[
        'bg-white rounded-[var(--radius-xl)]',
        'border border-[var(--color-border)]',
        'shadow-[var(--shadow-xs)]',
        paddings[padding],
        className,
      ].filter(Boolean).join(' ')}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card.Header — title row with optional subtitle and action
 */
Card.Header = function CardHeader({ title, subtitle, action, className = '' }) {
  return (
    <div
      className={[
        'flex items-center justify-between gap-4',
        'px-5 sm:px-6 py-4',
        'border-b border-[var(--color-border)]',
        className,
      ].join(' ')}
    >
      <div>
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)] leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

/**
 * Card.Body — padded interior, scrollable if needed
 */
Card.Body = function CardBody({ className = '', children }) {
  return (
    <div className={['px-5 sm:px-6 py-4 sm:py-5', className].join(' ')}>
      {children}
    </div>
  );
};

/**
 * Card.Footer — bottom strip for actions
 */
Card.Footer = function CardFooter({ className = '', children }) {
  return (
    <div
      className={[
        'px-5 sm:px-6 py-3 sm:py-4',
        'border-t border-[var(--color-border)]',
        'bg-[var(--color-surface)]',
        'rounded-b-[var(--radius-xl)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
};

/**
 * PageHeader
 *
 * Top-of-page title + optional subtitle + optional action area.
 * Used at the top of every page content area.
 *
 * Usage:
 *   <PageHeader
 *     title="Fulfillment Tracker"
 *     subtitle="938 records"
 *     action={<UploadButton />}
 *   />
 */
export function PageHeader({ title, subtitle, action, breadcrumb, className = '' }) {
  return (
    <div
      className={[
        'flex items-start justify-between gap-4',
        'px-5 sm:px-6 lg:px-8 py-4 sm:py-5',
        'border-b border-[var(--color-border)]',
        'bg-white shrink-0',
        className,
      ].join(' ')}
    >
      <div className="min-w-0">
        {breadcrumb && (
          <div className="flex items-center gap-1.5 mb-1">
            {breadcrumb}
          </div>
        )}
        <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-primary)] tracking-[-0.01em] leading-tight truncate">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-[var(--color-text-muted)] mt-0.5">{subtitle}</p>
        )}
      </div>
      {action && (
        <div className="shrink-0 flex items-center gap-2">{action}</div>
      )}
    </div>
  );
}

/**
 * EmptyState
 *
 * Centered empty state with icon, title, description, optional action.
 *
 * Usage:
 *   <EmptyState
 *     icon={<SearchIcon />}
 *     title="No results"
 *     description="Try adjusting your filters."
 *   />
 */
export function EmptyState({ icon, title, description, action, className = '' }) {
  return (
    <div
      className={[
        'flex flex-col items-center justify-center',
        'py-16 px-6 text-center',
        className,
      ].join(' ')}
    >
      {icon && (
        <div className="w-12 h-12 rounded-[var(--radius-xl)] bg-[var(--color-surface-alt)] flex items-center justify-center text-[var(--color-text-muted)] mb-4">
          {icon}
        </div>
      )}
      {title && (
        <p className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">{title}</p>
      )}
      {description && (
        <p className="text-xs text-[var(--color-text-muted)] max-w-xs leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Divider
 */
export function Divider({ className = '' }) {
  return (
    <hr className={['border-none border-t border-[var(--color-border)]', className].join(' ')} />
  );
}

export default Card;
